import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, Tooltip } from '@mui/material';
import { StatsBadge } from './statsBadge';

type RangeOption = {
  label: string;
  days: number;
};

type StatsRangeBadgeProps = {
  label: string;
  size?: 'sm' | 'md';
  rangeDays?: number;
  value: number | null;
  uniqueValue?: number | null;
  showRange?: boolean;
  onRangeChange: (rangeDays: number) => void;
  options?: RangeOption[];
};

export const StatsRangeBadge = ({
  label,
  size = 'sm',
  rangeDays = 365,
  value,
  uniqueValue,
  showRange = true,
  onRangeChange,
  options,
}: StatsRangeBadgeProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const rangeOptions = useMemo<RangeOption[]>(
    () =>
      options ?? [
        { label: t('Range12Months'), days: 365 },
        { label: t('Range90Days'), days: 90 },
        { label: t('Range30Days'), days: 30 },
      ],
    [options, t],
  );

  const rangeLabel =
    rangeOptions.find((opt) => opt.days === rangeDays)?.label ??
    t('Range12Months');

  const badgeLabel = showRange ? `${label} ${rangeLabel}` : label;

  const tooltipTitle = (
    <>
      {label}: {value ?? '--'}
      <br />
      {t('UniqueLabel')} {label.toLowerCase()}: {uniqueValue ?? '--'}
      <br />
      {t('SelectedPeriod')}: {rangeLabel}
    </>
  );

  return (
    <>
      <Tooltip title={tooltipTitle} placement='top' arrow>
        <span>
          <StatsBadge
            size={size}
            label={badgeLabel}
            value={value ?? '--'}
            onClick={(e) => setAnchorEl(e.currentTarget as HTMLElement)}
          />
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
      >
        {rangeOptions.map((option) => (
          <MenuItem
            key={option.days}
            selected={option.days === rangeDays}
            onClick={() => {
              onRangeChange(option.days);
              setAnchorEl(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

StatsRangeBadge.defaultProps = {
  size: 'sm',
  rangeDays: 365,
  uniqueValue: undefined,
  showRange: true,
  options: undefined,
};
